import {
	init as initSentry,
	type BrowserOptions,
	getCurrentScope,
	browserTracingIntegration,
} from "@sentry/react";

type MonitoringOptions = Pick<BrowserOptions, "beforeSend"> & {
	readonly sentryDsn: string | undefined;
	readonly sentryEnvironment: string | undefined;
	readonly number: string;
	readonly commitId: string;
	readonly buildTimestamp: string;
	/**
	 * Drop expected AbortError/cancellation events (e.g. AbortController aborts
	 * on effect cleanup/unmount, TanStack Query cancellations) before reporting.
	 * Opt-in: these are benign but otherwise arrive as unhandled rejections and
	 * create Sentry noise.
	 */
	readonly filterAbortErrors?: boolean;
};

/**
 * Aborts surface as a DOMException with name "AbortError", a lib AbortError, or
 * a TanStack Query "CancelledError". Match by name rather than message, which
 * varies by browser/locale ("signal is aborted without reason", "The operation
 * was aborted", "The user aborted a request").
 */
function isAbortError(err: unknown): boolean {
	return (
		err instanceof Error &&
		(err.name === "AbortError" || err.name === "CancelledError")
	);
}

function initMonitoring({
	number,
	commitId,
	buildTimestamp,
	sentryDsn,
	sentryEnvironment,
	beforeSend,
	filterAbortErrors = false,
}: MonitoringOptions) {
	if (sentryDsn) {
		initSentry({
			dsn: sentryDsn,
			environment: sentryEnvironment,
			release: commitId,
			integrations: [browserTracingIntegration()],
			normalizeDepth: 6,
			beforeSend(event, hint) {
				if (filterAbortErrors && isAbortError(hint.originalException)) {
					return null;
				}
				return beforeSend ? beforeSend(event, hint) : event;
			},
		});
	}

	const buildTime = new Date(parseInt(buildTimestamp, 10) * 1_000);
	// eslint-disable-next-line no-console
	console.log("App version", number, commitId, buildTime.toISOString());
	// eslint-disable-next-line no-console
	console.log("Sentry initialised", !!sentryDsn);

	getCurrentScope().setExtras({
		launchTime: new Date().toISOString(),
		versionNumber: number,
		versionId: commitId,
		buildTime: buildTime.toISOString(),
	});
}

export default initMonitoring;
